import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { enumValue, hasResponseCode } from '~/util';
import { EventService, PatientService, UserService, adminServiceEvents } from '~/services';

const propTypes = {
    auth: PropTypes.shape().isRequired,
    alert: PropTypes.shape().isRequired,
    client: PropTypes.shape().isRequired,
    history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
    mainRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape()]).isRequired,
};

const ServicesContext = React.createContext();

class ServicesProviderClass extends React.Component {
    api = {
        csrf_token:
            window.app.two_factor_auth_enrollment_in_progress?.csrf_token ||
            window.app.two_factor_auth_in_progress?.csrf_token ||
            window.app.csrf_token,
        retry: 0,
        retryLimit: 2,
        call: async (query, options) => {
            if (!options) {
                // eslint-disable-next-line no-param-reassign
                options = {};
            }
            if (!query.headers) {
                query.headers = {};
            }
            query.headers['X-Report-Year'] = this.props.client.reportYear;

            try {
                const result = await axios(query);

                // We're in maintenance mode! Get out of here!
                if (this.api.isMaintenanceMode(result)) {
                    this.props.auth.setAuth(false);
                    this.events.publish(UserService.events.userLoggedOut);
                    window.location.reload(true);
                }

                if (result.data.csrf_token) {
                    this.api.csrf_token = result.data.csrf_token;
                }
                this.api.retry = 0;
                return result;
            } catch (error) {
                if (error.response && error.response.data && error.response.data.csrf_token) {
                    this.api.csrf_token = error.response.data.csrf_token;
                }

                if (hasResponseCode(error, 'CSRF_ERROR')) {
                    // When we get a CSRF error, we should get an updated token
                    // in the response, so try the call once more.
                    if (this.api.retry < this.api.retryLimit) {
                        this.api.retry += 1;
                        if (query.data instanceof FormData) {
                            query.data.set('_csrf_token', this.api.csrf_token);
                        } else {
                            // eslint-disable-next-line no-underscore-dangle
                            query.data._csrf_token = this.api.csrf_token;
                        }
                        return this.api.call(query, options);
                    }
                }

                // Handle Standard Errors
                const responseStatus = error?.response?.status;
                const isErrorHandled =
                    options?.handledErrors === true || options?.handledErrors?.[responseStatus];
                switch (responseStatus) {
                    case 400:
                        if (hasResponseCode(error, 'UPDATE_COLLISION_ERROR')) {
                            this.events.publish(
                                EventService.EVENTS.update_collision,
                                error.response
                            );
                            break;
                        }
                        if (options?.handledErrors?.[responseStatus] === false) {
                            // Specifically not handling validation errors
                            // Publish api_error event to ApiErrorListener
                            this.events.publish(EventService.EVENTS.api_error, error.response);
                        }
                        break;
                    case 401:
                    case 403:
                        if (
                            this.props.auth.authenticated &&
                            hasResponseCode(error, 'NOT_AUTHENTICATED_ERROR')
                        ) {
                            // Show the error after redirecting to login page,
                            // but clear it when going to any other routes
                            this.props.alert.set('You have been logged out', 'danger', 1);
                            this.services.user.onLogout();
                            this.api.retry = 0;
                            return false;
                        }
                        if (!isErrorHandled) {
                            this.events.publish(EventService.EVENTS.api_error, error.response);
                        }
                        break;
                    case 404:
                        if (!isErrorHandled) {
                            this.props.history.push('/not-found');
                            return false;
                        }
                        break;
                    case 500:
                        if (!isErrorHandled) {
                            this.events.publish(EventService.EVENTS.api_error, error.response);
                        }
                        break;
                    default:
                        if (!isErrorHandled) {
                            // Publish api_error event to ApiErrorListener
                            this.events.publish(EventService.EVENTS.api_error, error.response);
                            // TODO: Figure out how we want to handle
                            //  defaultApiResponseHandler here?
                        }
                }

                throw error;
            }
        },
        isMaintenanceMode(response) {
            if (!response?.headers['x-response-code']) return false;
            return (
                parseInt(response.headers['x-response-code']) ===
                enumValue(['response', 'code', 'MAINTENANCE_MODE'])
            );
        },
    };

    events = new EventService();

    services = {
        user: new UserService(this),
        patient: new PatientService(this),
        alert: this.props.alert,
        auth: this.props.auth,
        events: this.events,
        mainRef: this.props.mainRef,
        api: this.api,
    };

    constructor(props) {
        super(props);
        // init events from service hooks
        this.events.init(adminServiceEvents);
    }

    componentDidUpdate(prevProps) {
        Object.keys(this.services).forEach((x) => {
            if (typeof this.services[x].componentDidUpdate === 'function') {
                this.services[x].componentDidUpdate(prevProps, this.props);
            }
        });
    }

    componentWillUnmount() {
        // We used to need this for unsubscribing from events within the services,
        // but we probably won't need this anymore going forward.
        // Object.keys(this.services).forEach((x) => {
        //     if (typeof this.services[x].destructor === 'function') {
        //         this.services[x].destructor();
        //     }
        // });
    }

    render() {
        return (
            <ServicesContext.Provider
                value={{
                    ...this.services,
                }}
                {...this.props}
            />
        );
    }
}

ServicesProviderClass.propTypes = propTypes;

// when this context is refactored, this HOC should be replaced with the useHistory hook
function withHistory(Component) {
    return function WrappedComponent(props) {
        const history = useHistory();
        return <Component {...props} history={history} />;
    };
}

const ServicesProvider = withHistory(ServicesProviderClass);
export { ServicesContext, ServicesProvider };
